import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

const POLL_INTERVAL = 12000; // 12 seconds

/**
 * Polls the session-check endpoint to detect forced sign-outs.
 * Returns { kicked, reason } where kicked=true means the user
 * should see the SessionKickedModal.
 */
export function useSessionCheck(enabled = true) {
  const [kicked, setKicked] = useState(false);
  const [reason, setReason] = useState(null);
  const intervalRef = useRef(null);
  const kickedRef = useRef(false);

  const check = useCallback(async () => {
    if (kickedRef.current) return;
    try {
      const res = await axios.get(`${API}/auth/session-check`, { withCredentials: true });
      if (!res.data.valid && res.data.reason === 'device_replaced') {
        kickedRef.current = true;
        setKicked(true);
        setReason(res.data.reason);
      }
    } catch {
      // Network errors or 401s — don't treat as kicked
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      // If we've navigated to a page where session polling shouldn't run
      // (e.g. from /faq back to /), reset any prior "kicked" state so the
      // modal doesn't linger on public pages.
      if (kickedRef.current) {
        kickedRef.current = false;
        setKicked(false);
        setReason(null);
      }
      return;
    }
    // Delay first check to let auth finish
    const timeout = setTimeout(() => {
      check();
      intervalRef.current = setInterval(check, POLL_INTERVAL);
    }, 5000);
    return () => {
      clearTimeout(timeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, check]);

  return { kicked, reason };
}

/**
 * Same hook but for admin sessions.
 */
export function useAdminSessionCheck(enabled = true) {
  const [kicked, setKicked] = useState(false);
  const [reason, setReason] = useState(null);
  const intervalRef = useRef(null);
  const kickedRef = useRef(false);

  const check = useCallback(async () => {
    if (kickedRef.current) return;
    try {
      const res = await axios.get(`${API}/admin/auth/session-check`, { withCredentials: true });
      if (!res.data.valid && res.data.reason === 'device_replaced') {
        kickedRef.current = true;
        setKicked(true);
        setReason(res.data.reason);
      }
    } catch {
      // Ignore
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (kickedRef.current) {
        kickedRef.current = false;
        setKicked(false);
        setReason(null);
      }
      return;
    }
    const timeout = setTimeout(() => {
      check();
      intervalRef.current = setInterval(check, POLL_INTERVAL);
    }, 5000);
    return () => {
      clearTimeout(timeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, check]);

  return { kicked, reason };
}
